import { Avatar, Icon } from '@amelio/ui-library';
import { useTranslation } from 'react-i18next';

import { useKeyResultProgressCommit } from '../../hooks/useKeyResultProgressCommit';
import HelpInfoIcon from '../HelpInfoIcon';
import KeyResultProgressBar from '../KeyResultProgressBar';
import KeyResultProgressControl from '../KeyResultProgressControl';
import { KEY_RESULT_UNIT_ICON } from '../keyResultUnitIcon';
import { MeasurementSpine, MeasurementSpineHead, MeasurementSpineItem } from '../MeasurementSpine';
import { IconCircle } from '../surfaces';

import type { User } from '@/types/directory/User';
import type { KeyResult, ObjectiveOrDraft } from '@/types/objectives';

import {
    DETAIL_SECTION_TITLE,
    GLASS_SURFACE,
    progressBadgeClassName,
} from '@/components/objectives/objectiveStyles';
import { KeyResultUnit } from '@/constants/KeyResultUnit';
import { ObjectiveType } from '@/constants/ObjectiveType';
import i18n from '@/plugins/i18n';
import { formatPercent } from '@/utils/numberFormat';
import {
    clampPercent,
    computeKeyResultValueFromPercentage,
    isProgressComplete,
} from '@/utils/objectiveProgress';
import { formatKeyResultTarget } from '@/utils/objectivesFormat';
import { getFullName, getInitials, getProfilePictureUrl } from '@/utils/user';

interface ObjectiveDetailMeasurementsProps {
    objective: ObjectiveOrDraft;
    employeesById: Map<string, User>;
}

interface DetailKeyResultCardProps {
    objectiveId: string;
    isTeamObjective: boolean;
    keyResult: KeyResult;
    responsible?: User;
    /** False for a reader who may not change the objective: the bar and the value only read. */
    canEdit: boolean;
}

/** One key result: unit glyph, title, its responsible on a team objective, bar and value. */
function DetailKeyResultCard({
    objectiveId,
    isTeamObjective,
    keyResult,
    responsible,
    canEdit,
}: DetailKeyResultCardProps) {
    const { t } = useTranslation();
    const hasNumericTarget = keyResult.unit !== KeyResultUnit.YES_NO;
    const percentage = clampPercent(keyResult.percentage);
    const isComplete = isProgressComplete(percentage);
    const { commit, isPending } = useKeyResultProgressCommit({ objectiveId, keyResult });
    const lang = i18n.language;

    /** The value a given point on the bar stands for, in this measurement's own unit. */
    const valueAtPercentage = (barPercentage: number): number =>
        computeKeyResultValueFromPercentage({
            unit: keyResult.unit,
            startValue: keyResult.startValue,
            targetValue: keyResult.targetValue,
            percentage: barPercentage,
        });

    // The bar's own inverse of the server-computed percentage: clicking it reports a percentage,
    // converted back into `currentValue` and committed through the same `commit` as the value
    // control next to it, so one pending write holds both.
    const handleBarChange = (nextPercentage: number) => commit(valueAtPercentage(nextPercentage));

    // Hovering the bar previews the value the drop would set, not the abstract percentage: "36"
    // on a target of 50, "36 %", "36 $" or OUI/NON -- the same wording the value control beside
    // it already uses.
    const formatValue = (value: number): string =>
        keyResult.unit === KeyResultUnit.YES_NO
            ? t(value > 0 ? 'objectives.form.yes' : 'objectives.form.no')
            : formatKeyResultTarget(value, keyResult.unit, keyResult.currencySymbol, lang);

    const formatHoverValue = (hoveredPercentage: number): string =>
        formatValue(valueAtPercentage(hoveredPercentage));

    return (
        <div
            className={
                'flex flex-col gap-2 rounded-[16px] border border-white bg-white/95 px-6 py-5 shadow-sm backdrop-blur-[2px]'
            }
            data-testid={`objective-detail-key-result-${keyResult.id}`}
        >
            <div className={'flex flex-wrap items-center gap-2'}>
                {/* `obj-kr-unit-tile` carries the tile's size at both widths (app.css). */}
                <IconCircle className={'obj-kr-unit-tile bg-obj-tint'}>
                    <Icon
                        name={KEY_RESULT_UNIT_ICON[keyResult.unit]}
                        size={keyResult.unit === KeyResultUnit.YES_NO ? 'lg' : 'md'}
                        colorClassName={'text-navy'}
                    />
                </IconCircle>

                <div className={'flex min-w-0 flex-1 flex-col'}>
                    <p className={'text-navy text-[14px] font-medium'}>{keyResult.title}</p>

                    {isTeamObjective && responsible && (
                        <div className={'flex items-center gap-2'}>
                            <Avatar
                                src={getProfilePictureUrl(responsible.profilePictureUrl)}
                                alt={getFullName(responsible)}
                                initials={getInitials(responsible)}
                                size={'4xs'}
                            />

                            <span className={'text-obj-muted text-[11px]'}>
                                {getFullName(responsible)}
                            </span>
                        </div>
                    )}
                </div>

                <span
                    className={`${progressBadgeClassName(percentage)} text-[11px]`}
                    data-testid={`objective-detail-key-result-${keyResult.id}-percentage`}
                >
                    {formatPercent(percentage, lang)}
                </span>
            </div>

            {/* The bar and the value control share one row, indented under the title. The bar is
                always rendered -- a Yes/No key result reads it at 0% or 100%, exactly like a
                numeric one, instead of going missing. */}
            <div className={'obj-kr-row flex flex-wrap items-center'}>
                <KeyResultProgressBar
                    percentage={percentage}
                    onChange={canEdit ? handleBarChange : undefined}
                    complete={isComplete}
                    label={keyResult.title}
                    className={'obj-kr-track-detail min-w-[120px]'}
                    cursorClassName={'bg-primary/70'}
                    formatHoverValue={formatHoverValue}
                    disabled={isPending}
                    dataTestId={`objective-detail-key-result-${keyResult.id}-bar`}
                />

                <div className={'ml-auto flex items-center gap-2'}>
                    {canEdit ? (
                        <KeyResultProgressControl
                            keyResult={keyResult}
                            onCommit={commit}
                            isPending={isPending}
                        />
                    ) : (
                        <span
                            className={'text-navy text-[11px] font-medium whitespace-nowrap'}
                            data-testid={`objective-detail-key-result-${keyResult.id}-value`}
                        >
                            {formatValue(keyResult.currentValue)}
                        </span>
                    )}

                    {hasNumericTarget && (
                        <span
                            className={'text-obj-muted text-[11px] font-medium whitespace-nowrap'}
                        >
                            {`/${formatKeyResultTarget(
                                keyResult.targetValue,
                                keyResult.unit,
                                keyResult.currencySymbol,
                                lang,
                            )}`}
                        </span>
                    )}
                </div>
            </div>
        </div>
    );
}

/**
 * "Éléments de mesure": the glass section, the "Objectif principal" inset, then one white card
 * per key result hung off the dashed purple spine.
 */
export default function ObjectiveDetailMeasurements({
    objective,
    employeesById,
}: ObjectiveDetailMeasurementsProps) {
    const { t } = useTranslation();

    return (
        <div
            className={`obj-section-detail ${GLASS_SURFACE}`}
            data-testid={'objective-detail-key-results'}
        >
            <div className={'obj-section-content-gap flex flex-col'}>
                <div className={'obj-section-title-gap flex flex-col'}>
                    <div className={'flex items-center gap-3'}>
                        <Icon
                            name={'essential-sort'}
                            size={24}
                            colorClassName={'text-navy'}
                            strokeWidth={1.5}
                        />
                        <h3 className={DETAIL_SECTION_TITLE}>
                            {t('objectives.form.measurementElements')}
                        </h3>
                    </div>

                    {objective.canEdit && (
                        <div className={'obj-section-help-line flex items-start'}>
                            <HelpInfoIcon />
                            <p className={'text-obj-muted text-[14px] font-medium'}>
                                {t('objectives.form.measurementElementsHelp')}
                            </p>
                        </div>
                    )}
                </div>

                <MeasurementSpineHead
                    surfaceClassName={'bg-obj-vision-tint'}
                    dataTestId={'objective-detail-main-objective'}
                >
                    <div className={'flex min-w-0 flex-col gap-1.5'}>
                        <p className={'text-obj-gray text-[14px] font-medium'}>
                            {t('objectives.form.mainObjective')}
                        </p>
                        <h4 className={'text-navy line-clamp-2 text-[20px] font-medium'}>
                            {objective.title}
                        </h4>
                    </div>
                </MeasurementSpineHead>

                <MeasurementSpine>
                    {objective.keyResults.map((keyResult, index) => (
                        <MeasurementSpineItem
                            key={keyResult.id}
                            markerTop={20}
                            isLast={index === objective.keyResults.length - 1}
                        >
                            <DetailKeyResultCard
                                objectiveId={objective.id}
                                isTeamObjective={objective.type === ObjectiveType.TEAM}
                                keyResult={keyResult}
                                responsible={
                                    keyResult.responsibleEmployeeId
                                        ? employeesById.get(keyResult.responsibleEmployeeId)
                                        : undefined
                                }
                                canEdit={objective.canEdit}
                            />
                        </MeasurementSpineItem>
                    ))}
                </MeasurementSpine>
            </div>
        </div>
    );
}
